import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search as SearchIcon, Heart } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import CertifiedBadge from "@/components/CertifiedBadge";
import { api, formatApiError } from "@/lib/api";

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [genre, setGenre] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.get("/users/search/list", { params: { q, role, genre } });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);

  const toggleFav = async (id, currently) => {
    try {
      if (currently) await api.delete(`/favorites/${id}`);
      else await api.post("/favorites", { target_id: id });
      setRows((rs) => rs.map((r) => r.id === id ? { ...r, _favorited: !currently } : r));
      toast.success(currently ? "Removed from favorites" : "Added to favorites");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <SiteShell>
      <section className="container-page py-14 overflow-x-hidden">
        <h1 className="font-serif text-4xl tracking-tight">Discover authors & readers</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">Find the people whose next project you&apos;d love to be part of.</p>

        <form onSubmit={search} className="mt-8 card-soft grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Search</label>
            <div className="mt-1 relative">
              <SearchIcon size={16} className="absolute left-3 top-3.5 text-muted-foreground" />
              <input className="sb-input pl-9" placeholder="Name, headline, bio" value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-q" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Role</label>
            <select className="sb-input mt-1" value={role} onChange={(e) => setRole(e.target.value)} data-testid="search-role">
              <option value="">Any</option>
              <option value="author">Author</option>
              <option value="beta_reader">Beta Reader</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Genre</label>
            <input className="sb-input mt-1" placeholder="e.g. Fantasy" value={genre} onChange={(e) => setGenre(e.target.value)} data-testid="search-genre" />
          </div>
          <div className="sm:col-span-4 flex justify-end">
            <button type="submit" className="btn-primary" data-testid="search-submit">Search</button>
          </div>
        </form>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading && Array.from({ length: 6 }).map((_, i) => <div key={i} className="card-soft h-40 animate-pulse" />)}
          {!loading && rows.length === 0 && <div className="card-soft text-center py-16">No matches yet — try a different search.</div>}
          {rows.map((u) => (
            <Link
              key={u.id}
              to={`/profile/${u.id}`}
              className="card-soft block min-w-0 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-forest))] focus:ring-offset-2 rounded-2xl"
              data-testid={`discover-profile-card-${u.id}`}
              aria-label={`View ${u.profile?.display_name || "profile"}'s public profile`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-12 h-12 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center text-[hsl(var(--sb-forest-2))] font-serif font-semibold overflow-hidden shrink-0">
                  {u.profile?.avatar_url ? (
                    <img src={u.profile.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (u.profile?.display_name || "?")[0]?.toUpperCase()
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                    <div className="font-medium truncate max-w-full">{u.profile?.display_name || "Anonymous"}</div>
                    <CertifiedBadge isCertified={u.is_certified} size="sm" />
                  </div>
                  <div className="text-xs text-muted-foreground truncate max-w-full">{u.profile?.headline || u.roles?.join(" · ")}</div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(u.id, u._favorited); }}
                  className={`shrink-0 p-2 rounded-full hover:bg-muted ${u._favorited ? "text-[hsl(var(--sb-clay))]" : "text-muted-foreground"}`}
                  data-testid="search-favorite-toggle"
                  aria-label={u._favorited ? "Remove from favorites" : "Add to favorites"}
                  title="Favorite"
                >
                  <Heart size={16} className={u._favorited ? "fill-current" : ""} />
                </button>
              </div>
              <p className="mt-3 text-sm text-muted-foreground line-clamp-3 break-words">{u.profile?.bio || "No bio yet."}</p>
              <div className="mt-4 flex flex-wrap gap-2 min-w-0">
                {(u.profile?.genres || []).slice(0, 4).map((g) => <span key={g} className="chip max-w-full truncate">{g}</span>)}
              </div>
              <div className="mt-4 text-xs text-[hsl(var(--sb-forest-2))] font-medium" data-testid={`discover-view-profile-${u.id}`}>
                View profile →
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link to="/favorites" className="btn-ghost border"><Heart size={14} /> View your favorites</Link>
        </div>
      </section>
    </SiteShell>
  );
}
